import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { Checkbox } from '@atlaskit/checkbox/checkbox';
import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea/text-area';
import { token } from '@atlaskit/tokens';

import { OTHER_ID } from '../../../../../../../../common/types/form/form-fields/field-values';
import { asOptionsValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import { asFormOptionField } from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import {
	useCurrentValue,
	useFieldInvalid,
	useFieldRequired,
	useFormField,
	useFormRendererActions,
} from '../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

const styles = cssMap({
	placeholder: {
		marginTop: token('space.500'),
		marginRight: token('space.500'),
		marginBottom: token('space.500'),
		marginLeft: token('space.500'),
	},
	option: { marginTop: token('space.050') },
});

export const EditCheckboxInput = ({
	fieldId,
	fieldInputId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue, useFormField, useFormRendererActions);
	const intl = useIntl();

	const currentValue = asOptionsValue(useCurrentValue({ fieldId }));
	const formField = useFormField({ fieldId });
	const formOptionField = asFormOptionField(formField);
	const { updateModifiedValue } = useFormRendererActions();
	const isInvalid = useFieldInvalid({ fieldId });
	const isRequired = useFieldRequired({ fieldId });
	const [otherTextInputValue, setOtherTextInputValue] = useState(currentValue?.otherOptionText);
	useEffect(() => {
		setOtherTextInputValue(currentValue?.otherOptionText);
	}, [currentValue?.otherOptionText, setOtherTextInputValue]);

	const onChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			const selectedOptionId = event.target.value;
			let options = currentValue?.options ?? [];
			if (options.some((option) => option.id === selectedOptionId)) {
				// "Unchecking" a checked option
				options = options.filter((option) => option.id !== selectedOptionId);
			} else if (selectedOptionId !== OTHER_ID) {
				const selectedOption = formOptionField?.options?.find(
					(option) => option.id === selectedOptionId,
				);
				if (selectedOption !== undefined) {
					options = [...options, selectedOption];
				}
			}
			const otherOptionText =
				selectedOptionId !== OTHER_ID
					? currentValue?.otherOptionText
					: currentValue?.otherOptionText === undefined
						? ''
						: undefined;
			if (options.length === 0 && otherOptionText === undefined) {
				updateModifiedValue(fieldId, null);
			} else {
				updateModifiedValue(fieldId, { otherOptionText, options });
			}
		},
		[
			currentValue?.options,
			currentValue?.otherOptionText,
			fieldId,
			formOptionField?.options,
			updateModifiedValue,
		],
	);

	const debouncedUpdateModifiedValue = useMemo(
		() => debounce(updateModifiedValue, 500),
		[updateModifiedValue],
	);

	const onOtherOptionTextChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>): void => {
			setOtherTextInputValue(event.target.value);
			debouncedUpdateModifiedValue(fieldId, {
				otherOptionText: event.target.value,
				options: currentValue?.options ?? [],
			});
		},
		[currentValue?.options, debouncedUpdateModifiedValue, fieldId, setOtherTextInputValue],
	);

	if (formOptionField === undefined) {
		return <></>;
	}

	const otherOption = { id: OTHER_ID, label: intl.formatMessage(messages.otherOption) };

	const getCheckboxId = (optionId: string, index: number): string | undefined => {
		if (fg('jsm-reqin-fieldset-update')) {
			return `${fieldInputId}-option-${optionId}`;
		}
		return index === 0 ? fieldInputId : undefined;
	};

	return (
		<Box
			testId={`field-input-checkbox-edit-${fieldId}`}
			aria-invalid={isInvalid}
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: to unblock React 18.2.0 -> 18.3.1 version bump in Jira
			title={fg('jsm-reqin-fieldset-update') ? undefined : formField?.label}
			aria-describedby={
				isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
			}
		>
			{formOptionField.hasOtherOption || formOptionField.options.length ? (
				<>
					{(formOptionField.hasOtherOption
						? [...formOptionField.options, otherOption]
						: formOptionField.options
					).map((option, index) => (
						<Checkbox
							key={`${fieldInputId}-${option.id}-${option.label}`}
							id={getCheckboxId(option.id, index)}
							name={
								fg('jsm-reqin-fieldset-update')
									? fieldInputId
									: `${fieldInputId}-${option.id}-${option.label}`
							}
							testId={`field-input-checkbox-edit-${fieldId}-option-${option.id}`}
							value={option.id}
							label={option.label}
							isChecked={
								currentValue?.options.some((currentOption) => currentOption.id === option.id) ||
								(option.id === OTHER_ID && currentValue?.otherOptionText !== undefined)
							}
							onChange={onChange}
							aria-required={isRequired}
						/>
					))}
					{otherTextInputValue !== undefined && (
						<Box xcss={styles.option}>
							<TextArea
								appearance="standard"
								placeholder={intl.formatMessage(messages.otherOption)}
								minimumRows={2}
								isMonospaced={false}
								value={otherTextInputValue}
								resize="smart"
								spellCheck={false}
								onChange={onOtherOptionTextChange}
								testId={`field-input-checkbox-edit-other-${fieldId}`}
								aria-label={intl.formatMessage(messages.otherOptionTextLabel, {
									label: formField.label,
								})}
								aria-describedby={
									isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
								}
							/>
						</Box>
					)}
				</>
			) : (
				<Box xcss={styles.placeholder} />
			)}
		</Box>
	);
};
